import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import arrowIcon from '../../../assets/img/icons/arrow-right-black.svg';
import { CommonImage, CommonText, CommonFlex, ColoredText } from '../../../styledComponents/common';
import FilterDropDownStyle from '../../DropdownStyle/FilterDropDownStyle';
import Select from '../../ReactSelectDropdown';
import {
  DisplayCountTextSection,
  PaginationButton,
  TopPaginationContainer,
  TopPaginationSection,
  SelectionButton,
  SelectionContainer
} from '../paginationStyles';

const TopPagination = props => {
  const {
    totalCount = 0,
    currentPage = 1,
    onPageChange = () => {},
    pageSize = 10,
    itemName = 'items',
    padding,
    margin,
    selectedMembers = [],
    setSelectedAllMembers = () => {},
    clearAllSelections = () => {}
  } = props;

  const { t } = useTranslation();

  const totalPages = useMemo(() => {
    if (!totalCount || !pageSize || pageSize <= 0) return 1;
    const totalPageCount = Math.ceil(totalCount / pageSize);
    return totalPageCount || 1; // Ensure we always have at least 1 page
  }, [totalCount, pageSize]);

  const pageOptions = useMemo(() => {
    const length = totalPages || 1;

    return Array.from({ length }, (_, idx) => {
      return { label: idx + 1, value: idx + 1 };
    });
  }, [totalPages]);

  const dropdownStyles = FilterDropDownStyle({
    height: '28px',
    width: '50px',
    menuWidth: '50px',
    indicatorWidth: '32px',
    valuePadding: '0 8px',
  });

  // Calculate if all items on current page are selected
  const allCurrentPageSelected = selectedMembers && (
    selectedMembers.length === pageSize ||
    (currentPage * pageSize > totalCount &&
     selectedMembers.length === totalCount - (currentPage - 1) * pageSize)
  );

  // If data is invalid or pagination not needed, return null
  if (!totalCount || currentPage === 0 || totalPages < 2) {
    return null;
  }

  const onNext = () => {
    onPageChange(currentPage + 1);
  };

  const onPrevious = () => {
    onPageChange(currentPage - 1);
  };

  return (
    <CommonFlex justifyContent="space-between" alignItems="center">
      {selectedMembers && selectedMembers.length > 0 ? (
        <SelectionContainer>
          {props.selectedAllMembers ? (
            <CommonText>
              All {totalCount} members selected
              <SelectionButton onClick={() => clearAllSelections()}>
                Clear selection
              </SelectionButton>
            </CommonText>
          ) : allCurrentPageSelected ? (
            <CommonText>
              {selectedMembers.length} members selected
              <SelectionButton onClick={() => setSelectedAllMembers(true)}>
                Select all {totalCount} members
              </SelectionButton>
            </CommonText>
          ) : (
            <CommonText>
              {selectedMembers.length} members selected
            </CommonText>
          )}
        </SelectionContainer>
      ) : (
        <CommonText>
          {t('displaying')} {Math.min((currentPage - 1) * pageSize + 1, totalCount)}-
          {currentPage * pageSize < totalCount
            ? currentPage * pageSize
            : totalCount}{' '}
          {t('of')} {totalCount} {itemName}
        </CommonText>
      )}

      <TopPaginationSection>
        <PaginationButton
          onClick={onPrevious}
          previous
          disabled={currentPage === 1}
          size="28px"
        >
          <CommonImage src={arrowIcon} alt="previous" />
        </PaginationButton>
        <CommonText $label fontWeight="500">
          {t('showing_page')}
        </CommonText>
        <Select
          isSearchable
          value={{ label: currentPage, value: currentPage }}
          options={pageOptions}
          onChange={e => {
            if (e.value !== currentPage) {
              onPageChange(e.value);
            }
          }}
          styles={dropdownStyles}
          indicatorIconSize="18px"
        />
        <CommonText $label fontWeight="500">
          {t('of')} {totalPages}
        </CommonText>
        <PaginationButton
          onClick={onNext}
          disabled={currentPage === totalPages}
          size="28px"
        >
          <CommonImage src={arrowIcon} alt="next" />
        </PaginationButton>
      </TopPaginationSection>
    </CommonFlex>
  );
};

export default TopPagination;
